import { formatDayMonthYear } from "@/utils/formatDate";
import { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import VpnKeyOutlinedIcon from "@mui/icons-material/VpnKeyOutlined";
import toast from "react-hot-toast";
import { CustomButton } from "@/components/common/customButton";
import { DataStatusDot } from "@/components/dataCompleteness/DataCompletenessButton";
import { getUsersDataCompleteness } from "@/services/dataCompletenessApi";
import type { UserDataCompleteness } from "@/types/dataCompleteness";
import { useCurrentUser } from "@/context/CurrentUserContext";
import {
  createUser,
  getMe,
  listUsers,
  updateUser,
  USER_ROLES,
  type UserCreate,
  type UserRead,
  type UserRole,
  type UserRoleRead,
} from "@/pages/settings/users";
import "@/assets/css/Settings.css";
import "@/assets/css/SettingsUsers.css";
import "@/assets/css/Text-General.css";
import "@/assets/css/Button.css";

const LEGACY_ROLES = ["MANAGER", "ANALYST"] as const;

const ROLE_LABELS: Record<string, string> = {
  ADMIN: "Admin",
  EDITOR: "Editor",
  VIEWER: "Viewer",
  MANAGER: "Manager",
  ANALYST: "Analyst",
};

function formatRoleLabel(role: string): string {
  return ROLE_LABELS[role] ?? role;
}

function getRoleOptions(currentRole: UserRoleRead): readonly string[] {
  if (currentRole === "ADMIN" || USER_ROLES.includes(currentRole as UserRole)) {
    return USER_ROLES;
  }

  if (LEGACY_ROLES.includes(currentRole as (typeof LEGACY_ROLES)[number])) {
    return [...USER_ROLES, currentRole];
  }

  return USER_ROLES;
}

const emptyNewUser = (): UserCreate => ({
  email: "",
  name: "",
  role: "EDITOR",
  auth_type: "SSO",
});

function formatUserLastLogin(dateStr: string | null): string {
  if (!dateStr) {
    return "Never";
  }

  const date = new Date(dateStr);
  if (Number.isNaN(date.getTime())) {
    return "Never";
  }

  return formatDayMonthYear(date);
}

export default function SettingsUsers() {
  const { canEdit } = useCurrentUser();
  const [currentUser, setCurrentUser] = useState<UserRead | null>(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [users, setUsers] = useState<UserRead[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [resetUserId, setResetUserId] = useState<string | null>(null);
  const [resetPassword, setResetPassword] = useState("");
  const [newUser, setNewUser] = useState<UserCreate>(emptyNewUser);
  // Outstanding deal data per deal lead; users not listed are green.
  const [dataByUser, setDataByUser] = useState<
    Map<number, UserDataCompleteness>
  >(new Map());

  const loadUsers = useCallback(async () => {
    try {
      const [data, completeness] = await Promise.all([
        listUsers({ limit: 100 }),
        getUsersDataCompleteness(),
      ]);
      setUsers(data);
      if (completeness.success && completeness.data) {
        setDataByUser(new Map(completeness.data.map((d) => [d.user_id, d])));
      }
    } catch {
      toast.error("Failed to load users");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;

    void (async () => {
      try {
        const me = await getMe();
        if (!cancelled) {
          setCurrentUser(me);
        }
      } catch {
        if (!cancelled) {
          setCurrentUser(null);
        }
      } finally {
        if (!cancelled) {
          setAuthLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (authLoading) {
      return;
    }
    if (currentUser?.role !== "ADMIN") {
      setLoading(false);
      return;
    }

    void loadUsers();
  }, [authLoading, currentUser?.role, loadUsers]);

  const handleCreate = async () => {
    try {
      await createUser({
        ...newUser,
        is_active: true,
        password: newUser.auth_type === "SSO" ? null : newUser.password,
      });
      toast.success("User created");
      setDialogOpen(false);
      setNewUser(emptyNewUser());
      setLoading(true);
      await loadUsers();
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Failed to create user",
      );
    }
  };

  const handleRoleChange = async (userId: string, role: string) => {
    try {
      await updateUser(userId, { role: role as UserRole });
      toast.success("Role updated");
      await loadUsers();
    } catch {
      toast.error("Failed to update role");
    }
  };

  const handleToggleActive = async (userId: string, isActive: boolean) => {
    try {
      await updateUser(userId, { is_active: isActive });
      toast.success(isActive ? "User activated" : "User deactivated");
      await loadUsers();
    } catch {
      toast.error("Failed to update status");
    }
  };

  const handleResetPassword = async () => {
    if (!resetUserId || !resetPassword) {
      return;
    }

    try {
      await updateUser(resetUserId, { password: resetPassword });
      toast.success(
        "Password reset. User will be prompted to change it on next login.",
      );
      setResetDialogOpen(false);
      setResetPassword("");
      setResetUserId(null);
      await loadUsers();
    } catch {
      toast.error("Failed to reset password");
    }
  };

  if (authLoading) {
    return (
      <Box
        className="settings-users-page"
        sx={{ display: "flex", justifyContent: "center", py: 8 }}
      >
        <CircularProgress size={32} />
      </Box>
    );
  }

  if (!currentUser || currentUser.role !== "ADMIN") {
    return (
      <Box className="settings-users-page" sx={{ py: 4, textAlign: "center" }}>
        <Typography color="text.secondary">
          You do not have permission to manage users.
        </Typography>
        <Typography color="text.secondary" sx={{ mt: 1 }}>
          Contact your admin or email sjenkins@tenfiftycapital.com
        </Typography>
      </Box>
    );
  }

  return (
    <Box className="settings-users-page">
      <Box className="settings-users-header">
        <Typography component="h1" className="text-general-page-header" sx={{ mb: 0 }}>
          Users
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon sx={{ fontSize: 18 }} />}
          onClick={() => setDialogOpen(true)}
          className="primary-button-solid settings-users-add-btn"
          disableRipple
          disabled={!canEdit}
        >
          Add User
        </Button>
      </Box>

      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add User</DialogTitle>
        <DialogContent
          sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}
        >
          <TextField
            label="Email"
            type="email"
            value={newUser.email}
            onChange={(event) =>
              setNewUser({ ...newUser, email: event.target.value })
            }
            fullWidth
            size="small"
          />
          <TextField
            label="Display Name"
            value={newUser.name}
            onChange={(event) =>
              setNewUser({ ...newUser, name: event.target.value })
            }
            fullWidth
            size="small"
          />
          <FormControl fullWidth size="small">
            <InputLabel id="new-user-role-label">Role</InputLabel>
            <Select
              labelId="new-user-role-label"
              label="Role"
              value={newUser.role}
              onChange={(event) =>
                setNewUser({
                  ...newUser,
                  role: event.target.value as UserCreate["role"],
                })
              }
            >
              {USER_ROLES.map((role) => (
                <MenuItem key={role} value={role}>
                  {formatRoleLabel(role)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth size="small">
            <InputLabel id="new-user-auth-label">Auth Type</InputLabel>
            <Select
              labelId="new-user-auth-label"
              label="Auth Type"
              value={newUser.auth_type}
              onChange={(event) =>
                setNewUser({
                  ...newUser,
                  auth_type: event.target.value as UserCreate["auth_type"],
                })
              }
            >
              <MenuItem value="SSO">SSO (Microsoft)</MenuItem>
              <MenuItem value="PASSWORD">Password</MenuItem>
            </Select>
          </FormControl>
          {newUser.auth_type === "PASSWORD" && (
            <>
              <TextField
                label="Initial Password"
                type="password"
                value={newUser.password ?? ""}
                onChange={(event) =>
                  setNewUser({ ...newUser, password: event.target.value })
                }
                placeholder="Min 8 characters"
                fullWidth
                size="small"
              />
              <Typography variant="caption" color="text.secondary">
                User will be required to change this on first login.
              </Typography>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <CustomButton
            text="Cancel"
            handleButtonPress={() => setDialogOpen(false)}
            isButtonDisabled={false}
            solid={false}
          />
          <CustomButton
            text="Create"
            handleButtonPress={() => void handleCreate()}
            isButtonDisabled={
              !newUser.email ||
              !newUser.name ||
              (newUser.auth_type === "PASSWORD" && !newUser.password)
            }
            solid
          />
        </DialogActions>
      </Dialog>

      <Dialog
        open={resetDialogOpen}
        onClose={() => setResetDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Reset Password</DialogTitle>
        <DialogContent
          sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}
        >
          <TextField
            label="New Password"
            type="password"
            value={resetPassword}
            onChange={(event) => setResetPassword(event.target.value)}
            placeholder="Min 8 characters"
            fullWidth
            size="small"
          />
          <Typography variant="caption" color="text.secondary">
            User will be required to change this on next login.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <CustomButton
            text="Cancel"
            handleButtonPress={() => setResetDialogOpen(false)}
            isButtonDisabled={false}
            solid={false}
          />
          <CustomButton
            text="Reset Password"
            handleButtonPress={() => void handleResetPassword()}
            isButtonDisabled={!resetPassword || resetPassword.length < 8}
            solid
          />
        </DialogActions>
      </Dialog>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress size={32} />
        </Box>
      ) : (
        <TableContainer className="settings-users-table-wrap">
          <Table className="settings-users-table">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Auth</TableCell>
                <TableCell>Role</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Data</TableCell>
                <TableCell>Last Login</TableCell>
                <TableCell align="right" />
              </TableRow>
            </TableHead>
            <TableBody>
              {Array.isArray(users) &&
                users.length > 0 &&
                users.map((user) => (
                  <TableRow key={user.id}>
                    <TableCell className="settings-users-name">
                      {user.name}
                    </TableCell>
                    <TableCell className="settings-users-email">
                      {user.email}
                    </TableCell>
                    <TableCell>
                      <span
                        className={
                          user.auth_type === "SSO"
                            ? "settings-users-auth-badge settings-users-auth-badge--sso"
                            : "settings-users-auth-badge settings-users-auth-badge--password"
                        }
                      >
                        {user.auth_type === "SSO" ? "SSO" : "Password"}
                      </span>
                    </TableCell>
                    <TableCell>
                      <FormControl size="small" sx={{ minWidth: 112 }}>
                        <Select
                          value={user.role}
                          onChange={(event) =>
                            void handleRoleChange(user.id, event.target.value)
                          }
                          className="settings-users-role-select"
                          displayEmpty
                          disabled={!canEdit}
                        >
                          {getRoleOptions(user.role as UserRoleRead).map((role) => (
                            <MenuItem key={role} value={role}>
                              {formatRoleLabel(role)}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </TableCell>
                    <TableCell>
                      <Box className="settings-users-status-cell">
                        <Switch
                          checked={user.is_active}
                          onChange={(event) =>
                            void handleToggleActive(
                              user.id,
                              event.target.checked,
                            )
                          }
                          size="small"
                          className="settings-users-switch"
                          disabled={!canEdit}
                        />
                        <span
                          className={
                            user.is_active
                              ? "settings-users-status-badge settings-users-status-badge--active"
                              : "settings-users-status-badge settings-users-status-badge--inactive"
                          }
                        >
                          {user.is_active ? "Active" : "Inactive"}
                        </span>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <DataCompletenessCell
                        data={dataByUser.get(Number(user.id))}
                      />
                    </TableCell>
                    <TableCell className="settings-users-last-login">
                      {formatUserLastLogin(user.last_login_at)}
                    </TableCell>
                    <TableCell align="right">
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "flex-end",
                          gap: 0.25,
                        }}
                      >
                        {user.auth_type === "PASSWORD" && (
                          <IconButton
                            size="small"
                            aria-label="Reset password"
                            title="Reset Password"
                            className="settings-users-key-btn"
                            disabled={!canEdit}
                            onClick={() => {
                              setResetUserId(user.id);
                              setResetDialogOpen(true);
                            }}
                          >
                            <VpnKeyOutlinedIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}

function DataCompletenessCell({ data }: { data?: UserDataCompleteness }) {
  const red = data?.status === "red";
  const parts: string[] = [];
  if (data?.missing_kill_reason) {
    parts.push(
      `${data.missing_kill_reason} killed ${data.missing_kill_reason === 1 ? "deal" : "deals"} without a kill reason`,
    );
  }
  if (data?.missing_advisor_fees) {
    parts.push(
      `${data.missing_advisor_fees} ${data.missing_advisor_fees === 1 ? "deal" : "deals"} missing advisor fee quotes`,
    );
  }
  return (
    <Tooltip title={red ? parts.join("; ") : "Up to date"}>
      <Box
        component="span"
        sx={{ display: "inline-flex", alignItems: "center", gap: 0.75 }}
      >
        <DataStatusDot red={red} size={10} />
        {red ? "Needs input" : "Up to date"}
      </Box>
    </Tooltip>
  );
}
